import Button from '@mui/material/Button';
import Card from "@mui/material/Card";
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import PageBlueHeader from "components/PageBlueHeader";
import Cookies from "js-cookie";
import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { useUserData } from "context/UserDataContext";
import { apiRequest } from "utils/api";
import { useLanguage } from "context/LanguageContext";
import { downloadBulkUploadTemplate } from "utils/bulkUploadTemplate";
import { isUsEmpresa as checkIsUsEmpresa } from "utils/empresaCountry";

function SubirExcel() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);


  const { user, loading: authLoading } = useAuth();
  const { userData, empresaData } = useUserData();
  const { t, lang } = useLanguage();
  const isUsEmpresa = checkIsUsEmpresa(userData, empresaData);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  useEffect(() => {
    // Si Firebase está cargando, esperar
    if (authLoading) {
      return;
    }

    // Si no hay usuario autenticado, redirigir al login
    if (!user) {
      navigate("/authentication/sign-in");
      return;
    }

    // Si hay usuario, cargar datos
    obtenerYGuardarUserId();
  }, [navigate, user, authLoading]);


  const obtenerYGuardarUserId = async () => {
    try {
      const response = await apiRequest('/datos-usuario', {
        method: 'GET',
      });
  
      if (!response.ok) throw new Error('Error al obtener el userId');
      
      const data = await response.json();
      Cookies.set('userId', data.id);
    } catch (error) {
      console.error('Error al obtener y guardar userId:', error);
    }
  };


  const registrarAccion = async (accion, detalles = '') => {
    try {
      const userId = Cookies.get('userId');
      if (!userId) {
        console.error('No se pudo obtener el userId de las cookies');
        return;
      }

      const bodyData = {
        userId,
        accion,
        detalles,
        origen: 'empresas'
      };
      
      // Validar que origen no sea null o undefined
      if (!bodyData.origen || bodyData.origen === null) {
        console.error('ERROR: origen es null o undefined, forzando a "empresas"');
        bodyData.origen = 'empresas';
      }
      
      
      const jsonBody = JSON.stringify(bodyData);
      
      const response = await apiRequest('/registrar-accion', {
        method: 'POST',
        body: jsonBody
      });
      

      if (!response.ok) throw new Error('Error al registrar la acción');
    } catch (error) {
      console.error('Error al registrar la acción:', error);
    }
  };

  const onDrop = (acceptedFiles) => {
    setFile(acceptedFiles[0]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: '.csv, .xlsx',
    multiple: false
  });

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      await downloadBulkUploadTemplate({ lang, isUsEmpresa });
    } catch (error) {
      console.error('Error al descargar plantilla:', error);
      alert(t('common.error'));
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setLoading(true);

    const formData = new FormData();
    formData.append("UPLOADCARE_PUB_KEY", "405eb44180f11fa7d9e3");
    formData.append("UPLOADCARE_STORE", "auto");
    formData.append("file", file);

    try {
      const response = await fetch("https://upload.uploadcare.com/base/", {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Archivo ${file.name} subido exitosamente con ID: ${result.file}`);
        try {
          await fetch('https://hook.us1.make.com/zq645rpparcrp93odyc7hacmggb4gjn5', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ file: result.file }),
          });
        } catch (webhookError) {
          console.error('Error al enviar al webhook:', webhookError);
        }
        const accion = "Subir Archivo";
        const detalles = "Planilla de usuarios subida exitosamente";
        await registrarAccion(accion, detalles);
        setOpenDialog(false);
      } else {
        const error = await response.json();
        console.error("Error:", error);
        alert(`Error al subir archivo: ${error.detail}`);
      }
    } catch (error) {
      console.error("Network error:", error);
      alert("Error de red al intentar subir el archivo");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <PageBlueHeader
        title={t('bulkUpload.pageTitle')}
        subtitle={t('bulkUpload.pageSubtitle')}
      />
      <ArgonBox pt={2} pb={6}>
        <Card>
          <ArgonBox p={3}>
            <ArgonBox
              mb={3}
              p={2.5}
              display="flex"
              flexWrap="nowrap"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              sx={{
                border: '1px solid #dbeafe',
                borderRadius: '12px',
                backgroundColor: '#f8fbff',
              }}
            >
              <ArgonTypography variant="body2" sx={{ color: '#475569', whiteSpace: 'nowrap' }}>
                {t('bulkUpload.downloadTemplateHint')}
              </ArgonTypography>
              <Button
                variant="contained"
                onClick={handleDownloadTemplate}
                disabled={downloadingTemplate}
                startIcon={
                  downloadingTemplate
                    ? <CircularProgress size={18} sx={{ color: 'white' }} />
                    : <FileDownloadOutlinedIcon />
                }
                sx={{
                  flexShrink: 0,
                  px: 3,
                  py: 1.25,
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '10px',
                  backgroundColor: '#1E86F6',
                  color: '#ffffff',
                  boxShadow: '0 4px 14px rgba(30, 134, 246, 0.25)',
                  '&:hover': {
                    backgroundColor: '#1570d4',
                    boxShadow: '0 6px 18px rgba(30, 134, 246, 0.32)',
                  },
                  '&:disabled': {
                    backgroundColor: '#94a3b8',
                    color: '#ffffff',
                  },
                }}
              >
                {t('bulkUpload.downloadTemplate')}
              </Button>
            </ArgonBox>
            <div {...getRootProps({ style: { border: '2px dashed #007bff', padding: '20px', textAlign: 'center' } })}>
              <input {...getInputProps()} />
              <p>{t('bulkUpload.dropzone')}</p>
            </div>
            {file && (
              <ArgonBox mt={2}>
                <p>{t('bulkUpload.selectedFile')} {file.name}</p>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={() => setOpenDialog(true)}
                  sx={{ py: 1.5 }}
                >
                  {t('bulkUpload.uploadBtn')}
                </Button>
              </ArgonBox>
            )}
          </ArgonBox>
        </Card>
      </ArgonBox>
      <Footer />

      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            padding: 0
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: '1.25rem', 
          fontWeight: 'bold', 
          padding: '24px 24px 8px 24px',
          textAlign: 'left'
        }}>
          {t('bulkUpload.dialogTitle')}
        </DialogTitle>
        <DialogContent sx={{ padding: '8px 24px 16px 24px' }}>
          <DialogContentText sx={{ 
            color: '#666',
            fontSize: '1rem',
            lineHeight: '1.5'
          }}>
            {t('bulkUpload.dialogText')}
          </DialogContentText>
          {loading && <CircularProgress sx={{ mt: 2 }} />}
        </DialogContent>
        <div style={{
          padding: '16px 24px 24px 24px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button 
            onClick={handleUpload}
            disabled={loading}
            style={{
              backgroundColor: loading ? '#ccc' : 'rgba(255, 62, 62, 0.9)',
              color: loading ? '#666' : 'white',
              fontSize: '1rem',
              fontWeight: '500',
              padding: '8px 16px',
              borderRadius: '8px',
              border: 'none',
              cursor: loading ? 'not-allowed' : 'pointer',
              outline: 'none',
              opacity: loading ? 0.7 : 1
            }}
            onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = 'rgba(255, 62, 62, 1)')}
            onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = 'rgba(255, 62, 62, 0.9)')}
          >
            {t('bulkUpload.confirm')}
          </button>
          <button 
            onClick={() => setOpenDialog(false)}
            style={{
              backgroundColor: 'transparent',
              color: '#1E86F6',
              fontSize: '1rem',
              fontWeight: '500',
              padding: '8px 16px',
              border: 'none',
              cursor: 'pointer',
              outline: 'none'
            }}
          >
            {t('bulkUpload.cancel')}
          </button>
        </div>
      </Dialog>
    </DashboardLayout>
  );
}

export default SubirExcel;
